import { FaClasses } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { BORDER, FLEX_CONTAINER_COLUMN, FLEX_CONTAINER_ROW_CENTERED, FLEX_ITEM_AUTO, JUSTIFY_CONTENT, MARGIN, PADDING, TEXT_COLOR, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { addToArrayMap, buildObjectMap, filterNotDefined, getMapKeys, getMapValues, includes, pushIfNew } from "@cervval.js/common/src/tools/collection.tools";
import { getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { ButtonStyle, CjsButton } from "../button/button";
import { CjsIconFa, IconFaOptions } from "../icon/iconFa";
import { getIconFaName } from "../icon/iconFa.tools";
import { IconFA6 } from "../icon/tools/fontAwesome6IconList.tools";
import { i18n } from "./i18n/i18n";
import { SelectionChange } from "./selectionModel/smartTableSelectionModel";
import { CjsSmartTable, EXPANDED_TABLE_ROW, SmartTableCell, SmartTableRow, compareSmartTableRowsByUid } from "./smartTable";
import { getVisibleColumnsCount } from "./smartTableColumn.tools";

export const SMART_TABLE_HIERARCHY_DEFAULT_WIDTH_ICON = 16;

export const SelectChildrenClass = "select-children";

export interface SmartTableHierarchyInfo<ItemData> {

    /** Depth of the item data in hierarchy */
    depth: number;

    /** Is collapsable */
    collapsable: boolean;

    /** If children are hidden */
    collapsed: boolean;

    /** If child row is hidden */
    childCollapsed: boolean;

    /** If row contains child in the hierarchy */
    isParent: boolean;

    /** If one predecessor in hierarchy is collapsed */
    ancestorCollapsed: boolean;

    /** Has a descendant that has been filtered */
    isInFlatList: boolean;

    /** Is the row is the last row in hierarchy depth */
    isLast: boolean;

    /** Is the row get any parent hierarchy */
    parentHierarchy?: SmartTableHierarchyInfo<ItemData>;
}

export interface SelectChildrenOptions<ItemData> {
    /** Align right in cell, by default true */
    alignRight?: boolean;

    /** Add a div with nb selected / nb children, by default true */
    displayNbSelectedChildren?: boolean;

    /** Display a string select all */
    displaySelectAllString?: "long" | "short";

    /** Classes to be added to text */
    cssClasses?: string[];

    /** Custom render */
    customRender?: (item: ItemData, nbChildren: number, nbSelected: number) => HTMLElement | null;

    /** Icon to replace text */
    icon?: Partial<IconFaOptions>;

    /** Expand on select all */
    expandOnSelect?: boolean;

    /** Do not dispatch selection event */
    doNotDispatchEvent?: boolean;

    /** Select/unselect all callback */
    selectCallback?: (items: ItemData[], selected: boolean) => void;

    /** Cursor */
    cursor?: string;
}

export interface SmartTableHierarchyOptions<ItemData> {

    /** Define the uid of column to display the hierarchy (default is first column excluding the checkbox column) */
    columnUid?: keyof ItemData;

    /** Parent background color before the children */
    inheritParentColor?: boolean;

    /** 
     * Get parent item
     * @param child to retrieve parent if defined
     * @param allRowsData 
     */
    getParentItem?: (child: ItemData, allRowsData: ItemData[]) => ItemData | undefined;

    /** Display hierarchy sections, stripes before */
    displayHierarchySection?: boolean;

    /** Collapse button width, in px */
    collapseButtonWidth?: number;

    /**
     * Add a toggle button to expand/collapse all row
     * Toggle btn is added in first column header
     * ! Use should enable "displayHeader" option to show toggle button
     */
    enableToggleButton?: boolean;

    /**
     * Add collapsed content to an item with the possibility to expand it by default
     */
    collapseContentRender?: (item: ItemData) => {

        /** 
         * If no content: No collapse content is added for current item (<=> row)
         * If content is defined: Content is added as collapsed element for current item (<=> row) 
         * */
        content: AcceptedHtmlElementContent | null,

        /**
         * Set true to expand the row by default
         */
        expanded?: boolean
    };

    /** If true, subRows will toggle when user click on parent row */
    enableToggleOnParentRowClick?: boolean | ((item: ItemData) => boolean);

    /**
     * Collapsed status of one item of the table
     * Called each time a set data is done for all displayed items
     * @param item which must be collapsed or not
     * @returns {true} If the specific item need to be collapsed / {false} if not
     */
    collapseItemByDefault?: (item: ItemData | null) => boolean;

    /** Select options */
    selectChildren?: SelectChildrenOptions<ItemData>;
}

/** 
 * Type of collapse button in a row 
 */
export type SmartTableCollapseButtonType = "hierarchy" | "child";

/** 
 * Config of collapse button in a row
 */
export interface SmartTableCollapseButton {

    /** Type of collapse button */
    type: SmartTableCollapseButtonType;

    /** Icon to show in the button */
    iconName: string;
}

/**
 * Icons that can be used to display collapse buttons in a row
 */
export enum SmartTableCollapseIcons {
    CHILD_COLLAPSED = "fa-plus",
    CHILD_EXPANDED = "fa-minus",
    HIERARCHY_COLLAPSED = "fa-angle-right",
    HIERARCHY_EXPANDED = "fa-angle-down"
}


/** Default hierarchy */
const defaultSmartTableHierarchyInfo: SmartTableHierarchyInfo<anyReallyAny> = {
    depth: 0,
    collapsed: false,
    childCollapsed: false,
    isParent: false,
    ancestorCollapsed: false,
    collapsable: false,
    isInFlatList: false,
    isLast: false
};

export class SmartTableHierarchyHelper<ItemData> {

    /** Listener key for toggle all on parent row click */
    private readonly ENABLE_TOGGLE_ON_PARENT_ROW_CLICK_LISTENER_KEY = "enableToggleOnParentRowClick";

    /** Toggle state of hierarchy */
    public toggleHierarchyState = true;

    /** Button used to toggle (collapse|expand) all hierarchy rows */
    public toggleHierarchyBtn = new CjsButton({
        icon: {
            name: this._getHierarchyBtnIcon()
        },
        tooltip: {
            title: this._getHierarchyBtnTooltipTitle(),
            delay: 200
        },
        cssClasses: ["toggle-hierarchy-all-btn"],
        cssStyle: ButtonStyle.NONE
    });

    /** Rows, parents and children accessors */
    private _rowsByData = new Map<ItemData, SmartTableRow<ItemData>>();
    public children = new Map<SmartTableRow<ItemData>, SmartTableRow<ItemData>[]>();
    public parent = new Map<SmartTableRow<ItemData>, SmartTableRow<ItemData>>();
    public selectChildrenDisplay = new Map<SmartTableRow<ItemData>, { content: HTMLElement, cell: SmartTableCell<ItemData>, container: HTMLElement }>();
    public validRange = new Map<SmartTableRow<ItemData>, SmartTableRow<ItemData>[]>();

    /* Max depth of the current overlooked hierarchy */
    public maxDepth = 0;

    /** Accessor for parent colors */
    private _backGroundColors = new Map<SmartTableRow<ItemData>, string>();

    /** Cache that contains selectable rows in the hierarchy */
    private _selectableHierarchicalRowsCache?: SmartTableRow<ItemData>[];

    constructor(private _smartTable: CjsSmartTable<ItemData>) {
        // Init toggle all button
        this._initHierarchyToggleAllBtn();

        // Clear cache
        this._smartTable.filterEvent.on(_ => this._clearSelectableHierarchicalRowsCache());
        this._smartTable.setDataEvent.on(_ => this._clearSelectableHierarchicalRowsCache());
        this._smartTable.renderFinishEvent.on(_ => this._installToggleRowOnParentClick());
    }

    /** Getter for selectable hierarchical rows cache */
    public get selectableHierarchicalRows(): SmartTableRow<ItemData>[] {
        // Return cache if exists and not empty
        if (this._selectableHierarchicalRowsCache?.length) { return this._selectableHierarchicalRowsCache; }

        const selectableRows: SmartTableRow<ItemData>[] = [];

        const allRowsData = filterNotDefined(this._smartTable.tableRows.map(row => this._smartTable.getRowItemData(row)));
        // Loop through all table rows to build a list of rows to select including hierarchical rows
        for (const row of this._smartTable.tableRows) {
            // Continue if row is filtered
            if (!row.displayed) { continue; }

            // Get row data to get the parent row
            const data = getVOFValue(row.data);
            if (!data) { continue; }

            // Push row in the select list if parent row is in the list of rows to select
            const parentData = this._smartTable.options.hierarchyOptions?.getParentItem?.(data, allRowsData);
            const parentRow = parentData ? this._rowsByData.get(parentData) : undefined;
            if (parentData === undefined && includes(selectableRows, parentRow, compareSmartTableRowsByUid) && this._smartTable.isSelectableRow(row)) {
                pushIfNew(selectableRows, row, compareSmartTableRowsByUid);
            }
        }

        // Set cache
        this._selectableHierarchicalRowsCache = selectableRows;

        return this._selectableHierarchicalRowsCache;
    }

    /** Return initial info for ItemData */
    protected _getDefaultHierarchy(): SmartTableHierarchyInfo<ItemData> {
        return { ...defaultSmartTableHierarchyInfo };
    }

    /** Clear selectable hierarchical rows cache */
    private _clearSelectableHierarchicalRowsCache(): void {
        this._selectableHierarchicalRowsCache = undefined;
    }

    /** 
     * Create rows list with updated hierarchy information from items list
     * update accessors _parent, _children and _rowsByData
     * if p.clear is undefined, hierarchy is cleared
     * @returns Sorted flat list of rows
     */
    public createRowsWithHierarchy(allRowsData: ItemData[], p?: { clear?: boolean }): SmartTableRow<ItemData>[] {
        // This function should not be called if getParentItem is not defined
        if (!this._smartTable.options.hierarchyOptions) { throw "getParentItem not implemented"; }

        // If hierarchy should be cleared, don't use rowsByUid to retrieve previous hierarchyInfo 
        const rowsByUid = p?.clear === false ? buildObjectMap(this._smartTable.tableRows, "uid") : undefined;

        // Always clear rowsByData, data items references was changed between two setData
        this._rowsByData.clear();
        this.children.clear();
        this.parent.clear();

        let rowUidIndex = 0;

        const visibleColumnsLength = getVisibleColumnsCount(this._smartTable.options.columns, this._smartTable.options.columnsConfig, this._smartTable.options.addFillColumn);

        // Record previous hierarchy info to don't clear it if needed
        const previousHierarchyInfoByRowUid: Record<string, SmartTableHierarchyInfo<ItemData> | undefined> = {};

        for (const item of allRowsData) {

            // Creation row for item and add row to accessor
            if (this._rowsByData.get(item) === undefined) {
                const uid = this._smartTable.options.getRowUid?.(item, rowUidIndex) ?? String(rowUidIndex);

                // Set current hierarchyInfo in previous storage
                previousHierarchyInfoByRowUid[uid] = rowsByUid?.get(uid)?.hierarchyInfo;

                const row: SmartTableRow<ItemData> = {
                    displayed: true,
                    data: item,
                    uid,
                    globalIndex: rowUidIndex,
                    selected: false,
                    hierarchyInfo: {
                        ...this._getDefaultHierarchy(),
                        ...previousHierarchyInfoByRowUid[uid] ?? {} // use previous hierarchyInfo if exist
                    }
                };

                rowUidIndex++;
                this._rowsByData.set(item, row);
            }

            const itemParent = this._smartTable.options.hierarchyOptions.getParentItem?.(item, allRowsData);

            // Update parent / children information for current row
            if (itemParent !== undefined) {

                const uid = this._smartTable.options.getRowUid?.(itemParent, rowUidIndex) ?? String(rowUidIndex);

                // Create parent information if needed
                let parentRow: SmartTableRow<ItemData> | undefined = this._rowsByData.get(itemParent);
                if (!parentRow) {

                    parentRow = {
                        displayed: true,
                        data: itemParent,
                        uid,
                        globalIndex: rowUidIndex,
                        selected: false,
                        hierarchyInfo: this._getDefaultHierarchy()
                    };

                    const previousParentHierarchyInfo = rowsByUid?.get(parentRow.uid)?.hierarchyInfo;
                    // If hierarchy is already set, use it to keep hierarchyState
                    if (previousParentHierarchyInfo && parentRow.hierarchyInfo) {
                        parentRow.hierarchyInfo = previousParentHierarchyInfo;
                    }

                    rowUidIndex++;
                    this._rowsByData.set(itemParent, parentRow);
                }


                const rowChild = this._rowsByData.get(item);
                const rowParent = this._rowsByData.get(itemParent);

                if (rowParent && rowChild && rowParent.hierarchyInfo) {
                    // Update parent accessor and collapsable attribute
                    this.parent.set(rowChild, rowParent);
                    const previousParentHierarchyInfo = rowsByUid?.get(rowParent.uid)?.hierarchyInfo;

                    // If hierarchy is already set, use it to keep hierarchyState
                    rowParent.hierarchyInfo.collapsed = previousParentHierarchyInfo?.collapsed ?? false;
                    rowParent.hierarchyInfo.isParent = true;
                    rowParent.hierarchyInfo.collapsable = true;

                    // Update children accessor
                    addToArrayMap(this.children, rowParent, rowChild);
                }
            } else {// If item don't have any parent, he's automatically a parent item
                const rowItem = this._rowsByData.get(item);

                if (rowItem && this._smartTable.options.hierarchyOptions.getParentItem) {
                    // Check if children array is initialized for this item
                    if (!this.children.has(rowItem)) {
                        this.children.set(rowItem, []);
                    }

                    // Update item hierarchy
                    if (rowItem.hierarchyInfo) {
                        // If hierarchy is already set, use it to keep hierarchyState
                        rowItem.hierarchyInfo.collapsed = rowItem.hierarchyInfo.collapsed ?? false;
                        rowItem.hierarchyInfo.isParent = true;
                        rowItem.hierarchyInfo.collapsable = Boolean(this.children.get(rowItem)?.length);
                    }
                }
            }

            // Set childTr to the row if need to show collapse content
            const collapseContentRender = this._smartTable.options.hierarchyOptions.collapseContentRender?.(item);
            if (collapseContentRender?.content != null) {
                rowUidIndex++;

                const rowChild = this._rowsByData.get(item);
                if (rowChild?.hierarchyInfo) {
                    rowChild.hierarchyInfo.collapsable = true;
                    // Use previous collapse state if exist
                    rowChild.hierarchyInfo.childCollapsed = previousHierarchyInfoByRowUid[rowChild?.uid]?.childCollapsed ?? !collapseContentRender.expanded;

                    const rowTdCollapseContent = createHtmlElement("td", {
                        innerHTML: createHtmlElement("div", {
                            innerHTML: collapseContentRender.content,
                            cssClass: [FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO]
                        }),
                        attributes: {
                            // Full width 
                            colspan: String(visibleColumnsLength)
                        }
                    });

                    // Build child tr
                    const rowTrCollapseContent = createHtmlElement("tr", {
                        innerHTML: rowTdCollapseContent,
                        cssClass: [EXPANDED_TABLE_ROW]
                    });
                    rowChild.childTr = rowTrCollapseContent;
                }
            }
        }

        const rows = getMapValues(this._rowsByData);
        // Update depth from root rows
        this._setDepth(undefined, rows, 0);

        // Force initial sort to match initial sort by uid, orders should be kept except for parent/child => sort descending uid 
        return this.sortRowsUsingHierarchy(rows, (a, b) => {
            return a.globalIndex - b.globalIndex;
        }).map((row, index) => {// Assign correct row globalIndex after sort with hierarchy
            row.globalIndex = index;
            return row;
        });

    }

    /** Set depth attribute in row for display and collapse state according to the options */
    private _setDepth(parent: SmartTableRow<ItemData> | undefined,
        rows: SmartTableRow<ItemData>[], depth: number): void {
        // If parent is undefined, seek for roots. If parent is defined, take its children
        const rootRows = !parent ?
            rows.filter(row => this.parent.get(row) === undefined) :
            this.children.get(parent);

        if (rootRows != null) {
            if (depth > this.maxDepth) { this.maxDepth = depth; }
            for (const rootRow of rootRows) {
                if (rootRow.hierarchyInfo) {
                    rootRow.hierarchyInfo.depth = depth;
                    this._setDepth(rootRow, rows, depth + 1);
                }
            }
        }
    }

    /** Set collapse state according to the collpase item by default status */
    public setDefaultCollapseState(rows: SmartTableRow<ItemData>[], parent: SmartTableRow<ItemData> | undefined = undefined): void {
        // If parent is undefined, seek for roots. If parent is defined, take its children
        const rootRows = !parent ?
            rows.filter(row => this.parent.get(row) === undefined) :
            this.children.get(parent);

        if (rootRows != null) {
            for (const rootRow of rootRows) {
                if (rootRow.hierarchyInfo) {
                    // Use override collapsed status function defined in options or status of the parent if not defined with a fallback to status of the row itself
                    rootRow.hierarchyInfo.collapsed = this._smartTable.options?.hierarchyOptions?.collapseItemByDefault?.(this._smartTable.getRowItemData(rootRow)) ?? rootRow.hierarchyInfo.collapsed;
                    // Indicate to the parent that one of the child is collapsed, keep initial state if not (Assume that it's at false but can be changed for implementation need elsewhere)
                    if (parent?.hierarchyInfo) {
                        parent.hierarchyInfo.childCollapsed = (parent?.hierarchyInfo && rootRow.hierarchyInfo.collapsed) ?? parent.hierarchyInfo.childCollapsed;
                    }
                    // Need to indicate that the parent is collapsed to the children, keep initial state if not (Assume that it's at false but can be changed for implementation need elsewhere)
                    rootRow.hierarchyInfo.ancestorCollapsed = (parent?.hierarchyInfo?.collapsed || parent?.hierarchyInfo?.ancestorCollapsed) ?? rootRow.hierarchyInfo.ancestorCollapsed;
                    // Adjust status on row
                    this.setDefaultCollapseState(rows, rootRow);
                }
            }
        }
    }

    /** Get row collapsable */
    public isCollapsable(row: SmartTableRow<ItemData>): boolean {
        return row.hierarchyInfo?.depth != null && Boolean(row.hierarchyInfo.collapsable);
    }

    /** Can show icon if the row contains at least one child and if there is no childTr or if the row is a parent in the hierarchy */
    public canShowCollapseOrExpandHierarchyIcon(row: SmartTableRow<ItemData>): boolean {
        return Boolean(this.children.get(row)?.length) && (row.childTr === undefined || Boolean(row.hierarchyInfo?.isParent));
    }

    /** Can show icon if there is childTr */
    public canShowCollapseOrExpandChildIcon(row: SmartTableRow<ItemData>): boolean {
        return row.childTr !== undefined;
    }

    /** Can insert child row content if there is a childTr to insert and if the child to insert is not collapsed */
    public canInsertRowChild(row: SmartTableRow<ItemData>): boolean {
        return row.childTr !== undefined && !Boolean(row.hierarchyInfo?.childCollapsed);
    }

    /** Toggle (collapse | expand) row */
    public toggleCollapseOrExpandHierarchy(row: SmartTableRow<ItemData>): void {
        if (!row.hierarchyInfo) { return; }

        // Set collapsed attribute and propagate to children
        row.hierarchyInfo.collapsed = !row.hierarchyInfo.collapsed;
        this._setAncestorCollapsedFromRow(row, [row.uid]);
    }

    /** Toggle (collapse | expand) child row */
    public toggleCollapseOrExpandChildRow(row: SmartTableRow<ItemData>): void {
        if (!row.hierarchyInfo) { return; }

        // Set child collapsed attribute
        row.hierarchyInfo.childCollapsed = !row.hierarchyInfo.childCollapsed;

        // Remove child row if exists
        if (row.childTr) {
            row.childTr.remove();
        }
    }

    /** Update ancestorCollapsed status from one row after collapse change */
    private _setAncestorCollapsedFromRow(parent: SmartTableRow<ItemData>, alreadyVisited: string[]): void {
        if (parent.hierarchyInfo) {
            const children = this.children.get(parent);
            if (children !== undefined) {
                for (const child of children) {

                    if (child.hierarchyInfo) {
                        const previousAncestorCollapsed = child.hierarchyInfo.ancestorCollapsed;
                        if (parent.hierarchyInfo.collapsed || parent?.hierarchyInfo.ancestorCollapsed) {
                            child.hierarchyInfo.ancestorCollapsed = true;
                        } else {
                            child.hierarchyInfo.ancestorCollapsed = false;
                        }

                        // If attribute has changed, spread change in descendants
                        if (previousAncestorCollapsed !== child.hierarchyInfo!.ancestorCollapsed && !includes(alreadyVisited, child.uid)) {
                            this._setAncestorCollapsedFromRow(child, alreadyVisited.concat([child.uid]));
                        } else if (includes(alreadyVisited, child.uid)) {
                            // Loop detected in hierarchy
                            this._smartTable.hierarchyLoopEvent.emit({ row: parent });
                        }
                    }
                }
            }
        }
    }

    /** Update flat list attribute in ancestors after filter change */
    public updateFlatListAttributeInAncestors(): void {
        if (this._smartTable.options.hierarchyOptions &&
            // Update only if there is an active filter, if not, only use collapse information
            this._smartTable.filterHelper.getNotEmptySmartTableFilters().length > 0 || this._smartTable.filterHelper.hasAdvancedFiltered()) {
            for (const row of getMapKeys(this.parent)) {
                if (row.displayed) {
                    // Ensure the row will be displayed even if ancestor is collapse
                    row.hierarchyInfo!.isInFlatList = true;

                    // Set a list of uid in he flat list, to detect loops
                    const alreadyVisited: string[] = [row.uid];

                    let parent = this.parent.get(row);
                    // Iteration on ancestors to set the inFlatList attribute
                    while (parent?.hierarchyInfo && !includes(alreadyVisited, parent?.uid)) {
                        parent.hierarchyInfo.isInFlatList = true;
                        alreadyVisited.push(parent.uid);
                        parent = this.parent.get(parent);
                    }

                    if (includes(alreadyVisited, parent?.uid)) {
                        // Loop detected in hierarchy
                        this._smartTable.hierarchyLoopEvent.emit({ row });
                    }
                }
            }
        }
    }

    /** Clean flat list attribute before updated filter is applied 
     * attributes will be set to new value via updateFlatListAttributeInAncestors
     */
    public cleanFlatList(): void {
        if (this._smartTable.options.hierarchyOptions) {
            // Reset isInFlatList
            for (const data of getMapKeys(this._rowsByData)) {
                const row = this._rowsByData.get(data);
                if (row?.hierarchyInfo) {
                    row.hierarchyInfo.isInFlatList = false;
                }
            }
        }
    }

    /** Clear global hierarchy state */
    public clear(): void {
        this.children.clear();
        this.parent.clear();
        this._rowsByData.clear();
        this._clearSelectableHierarchicalRowsCache();
        this.cleanFlatList();
    }

    /** Reset cell background information */
    public resetCellBackground(): void {
        if (this._smartTable.options.hierarchyOptions?.inheritParentColor) {
            this._backGroundColors.clear();
        }
    }
    /** Store cell background information */
    public storeCellBackground(row: SmartTableRow<ItemData>, color: string): void {
        if (this._smartTable.options.hierarchyOptions?.inheritParentColor) {
            this._backGroundColors.set(row, color);
        }
    }

    /** Get colors from ancestors */
    public getBackgroundColorsFromAncestors(row: SmartTableRow<ItemData>): { colors: (string | undefined)[], isLast: boolean[] } {
        const colors: (string | undefined)[] = [];
        const isLast: boolean[] = [];

        if (this._smartTable.options.columns.length && this._smartTable.options.hierarchyOptions) {
            // Set a list of uid in he flat list, to detect loops
            const alreadyVisited: string[] = [row.uid];

            let parent = this.parent.get(row);
            // Iteration on ancestors to set the inFlatList attribute
            while (parent !== undefined && !includes(alreadyVisited, parent?.uid)) {
                // Test if parent is last. If parent last, push white background
                colors.push(this._smartTable.options.hierarchyOptions?.inheritParentColor ? this._backGroundColors.get(parent) : undefined);
                isLast.push(Boolean(parent.hierarchyInfo?.isLast));

                alreadyVisited.push(parent.uid);
                parent = this.parent.get(parent);
            }


            if (includes(alreadyVisited, parent?.uid)) {
                // Loop detected in hierarchy
                this._smartTable.hierarchyLoopEvent.emit({ row });
            }
        }

        return { colors, isLast };
    }

    /** Toggle (collapse | expand) all */
    public toggleCollapseExpandAll(collapsed: boolean, p?: { refresh: boolean, fromUserClick?: boolean }): void {
        if (!this._smartTable.options.hierarchyOptions) { return; }

        // Update hierarchyState for toggleAllButton
        this.toggleHierarchyState = !collapsed;

        for (const data of getMapKeys(this._rowsByData)) {
            const row = this._rowsByData.get(data);
            if (row?.hierarchyInfo) {
                if (row.hierarchyInfo.collapsable) {
                    row.hierarchyInfo.collapsed = collapsed;
                    row.hierarchyInfo.childCollapsed = collapsed;
                }
                if (row.hierarchyInfo.depth > 0) {
                    row.hierarchyInfo.ancestorCollapsed = collapsed;
                }
            }
        }

        if (p?.refresh !== false) {
            this._smartTable.updateDisplayableRows();
            // Refresh of smartTable will be trigger by timeline callback on 'allCollapseOrExpandEvent'
            if (!this._smartTable.options.withinTimeline) {
                this._smartTable.refresh();
            }
            this._smartTable.allCollapseOrExpandEvent.emit();
        }

        if (p?.fromUserClick !== true) {// Reset icon if toggleAll from js and not from user action
            this.toggleHierarchyBtn.options.setAndRefreshIfChanged({
                icon: {
                    name: this._getHierarchyBtnIcon()
                }
            });
        }
    }

    //#region sort
    /** 
     * Sort rows using hierarchy
     * Iteration on depth
     */
    public sortRowsUsingHierarchy(rows: SmartTableRow<ItemData>[],
        sortFunction: (row1: SmartTableRow<ItemData>, row2: SmartTableRow<ItemData>) => number): SmartTableRow<ItemData>[] {
        const sortedRows: SmartTableRow<ItemData>[] = [];

        // Get rows with no parents
        const rootRows = rows.filter(row => this.parent.get(row) === undefined);
        for (const rootRow of rootRows.sort(sortFunction)) {// sort root rows
            // Force root row isLast
            if (rootRow.hierarchyInfo) { rootRow.hierarchyInfo.isLast = true; }
            sortedRows.push(rootRow);

            // Apply sort in children 
            this._insertChildrenInSortedRows(rootRow, sortedRows, sortFunction);
        }

        return sortedRows;
    }

    /** Insert children row in sort result */
    private _insertChildrenInSortedRows(parent: SmartTableRow<ItemData>,
        sortedRows: SmartTableRow<ItemData>[],
        sortFunction: (row1: SmartTableRow<ItemData>, row2: SmartTableRow<ItemData>) => number): void {
        const childrenRows = this.children.get(parent);
        if (childrenRows !== undefined) {
            childrenRows.sort(sortFunction);

            childrenRows.forEach((child, i) => {
                sortedRows.push(child);
                this._insertChildrenInSortedRows(child, sortedRows, sortFunction);
                if (child.hierarchyInfo) {
                    child.hierarchyInfo.isLast = i === childrenRows.length - 1;
                    child.hierarchyInfo.parentHierarchy = parent.hierarchyInfo;
                }
            });
        }
    }
    //#endregion


    /** Retrieve the first parent of given hierarchy (stop at "depth = 0" or "no parent hierarchy") */
    public getFirstParentHierarchy(hierarchy: SmartTableHierarchyInfo<ItemData>): SmartTableHierarchyInfo<ItemData> | undefined {
        // Stop condition of recursives calls
        if (hierarchy.depth === 0 || !hierarchy.parentHierarchy) {
            return hierarchy;
        }
        return this.getFirstParentHierarchy(hierarchy.parentHierarchy);
    }

    /** Init the toggle all hierarchy btn */
    private _initHierarchyToggleAllBtn(): void {
        this.toggleHierarchyBtn.clickEvent.on(_ => {
            this.toggleCollapseExpandAll(this.toggleHierarchyState, { refresh: true, fromUserClick: true });
            this.toggleHierarchyBtn.options.setAndRefreshIfChanged({
                icon: {
                    name: this._getHierarchyBtnIcon()
                },
                tooltip: {
                    title: this._getHierarchyBtnTooltipTitle(),
                    delay: 200
                }
            });
        });

    }

    /** Retrieve the icon of hierarchy button, depends on toggle state of hierarchy */
    private _getHierarchyBtnIcon(): string {
        return this.toggleHierarchyState ? "fa-minimize" : "fa-maximize";
    }

    /** Retrieve the tolltip tittle of hierarchy button, depends on toggle state of hierarchy */
    private _getHierarchyBtnTooltipTitle(): string {
        return this.toggleHierarchyState ? this._smartTable.i18n.hierarchy.collapse.all : this._smartTable.i18n.hierarchy.expand.all;
    }

    /** Install click listener on parent row, will toggle/collapse all his sub rows */
    private _installToggleRowOnParentClick(): void {
        // Check if toggle on parent row click is enable in table options
        const enableToggleOnParentRowClick = this._smartTable.options.hierarchyOptions?.enableToggleOnParentRowClick;
        if (!enableToggleOnParentRowClick) { return; }

        for (const parentRow of this.children.keys()) {
            if (!parentRow.tr || getProp(parentRow.tr, this.ENABLE_TOGGLE_ON_PARENT_ROW_CLICK_LISTENER_KEY)) { continue; }// Should not happen, install should be called after rows creation

            const parentItem = this._smartTable.getRowItemData(parentRow);

            // Check enableToggleOnParentRowClick cb for current row (useless if 'enableToggleOnParentRowClick' is a boolean, already checked)
            if (typeof enableToggleOnParentRowClick === "function" && parentItem && !enableToggleOnParentRowClick(parentItem)) { continue; }

            parentRow.tr.addEventListener("click", () => {// Toggle row on click
                this._smartTable.toggleCollapseOrExpandRow(parentRow, "hierarchy");
                this._smartTable.refresh();
            });// No need to handle listener destroy, rows are destroyed after each render. Garbage collector will destroy all listeners linked to rows tr element.

            // Set flag listener registered on row
            setProp(parentRow.tr, this.ENABLE_TOGGLE_ON_PARENT_ROW_CLICK_LISTENER_KEY, true);
        }
    }

    /**
     * Returns true if current cell is matching the hierarchy column
     * Check columnUid option if defined, else check first column excluding checkbox column by default
     * @param cell Cell to get the column uid
     */
    public matchHierarchyColumn(cell: SmartTableCell<ItemData>): boolean {
        if (!this._smartTable.options.hierarchyOptions) {
            return false;
        }
        return this._smartTable.options.hierarchyOptions.columnUid ? this._smartTable.options.hierarchyOptions.columnUid === cell.column.uid : this._smartTable.matchFirstColumn(cell, { excludeCheckboxColumn: true });
    }


    /** Create select/unselect children div */
    public createSelectUnselect(cell: SmartTableCell<ItemData>, tdElement: HTMLElement): void {
        const tdFirstChild = tdElement.firstChild as HTMLElement;

        const selectChildrenOptions = this._smartTable.options.hierarchyOptions?.selectChildren;
        if (selectChildrenOptions == null) {
            return;
        }

        const itemData = cell.rowItemData;
        if (itemData == null) {
            return;
        }
        const row = cell.row;
        if (row.hierarchyInfo?.isParent) {
            const children = this.children.get(row);
            if (children == null) {
                return;
            }
            const nbChildren = children.length;
            if (nbChildren === 0) {
                return;
            }
            const nbSelected = children.filter(child => child.selected).length;

            let content: HTMLElement | null = null;
            if (selectChildrenOptions.customRender) {
                content = selectChildrenOptions.customRender(itemData, nbChildren, nbSelected);
            }

            const elts: AcceptedHtmlElementContent[] = [];
            if (selectChildrenOptions.displayNbSelectedChildren !== false) {
                elts.push(createHtmlElement("label", { innerHTML: nbSelected + "/" + nbChildren, cssClass: [PADDING.P_2] }));
            }

            if (selectChildrenOptions.displaySelectAllString) {
                const labels =
                    selectChildrenOptions.displaySelectAllString === "long" ? { select: i18n().hierarchy.selectChildren.unselectAll, unselect: i18n().hierarchy.selectChildren.selectAll } :
                        { select: i18n().hierarchy.selectChildren.shortUnselectAll, unselect: i18n().hierarchy.selectChildren.shortSelectAll };
                elts.push(createHtmlElement("label", { innerHTML: nbChildren === nbSelected ? labels.select : labels.unselect, cssClass: [PADDING.P_2, ...(selectChildrenOptions?.cssClasses ?? [BORDER.ALL])] }));
            }

            if (selectChildrenOptions.icon) {
                const iconOptions = this._getIconsFromIconName(selectChildrenOptions.icon);
                const iconOption = nbChildren === nbSelected ? iconOptions.all : nbSelected === 0 ? iconOptions.none : iconOptions.some;
                elts.push(new CjsIconFa({ ...iconOption, classes: [TEXT_COLOR.SUCCESS] }));
            }

            if (!content) {
                const classes = [FLEX_CONTAINER_ROW_CENTERED, SelectChildrenClass, PADDING.P_2];
                if (selectChildrenOptions.alignRight === false) {
                    classes.push(MARGIN.ME_AUTO, MARGIN.MS_0, JUSTIFY_CONTENT.START);
                    tdFirstChild.classList.add(MARGIN.MS_0);
                    ((tdFirstChild.children?.item(0)) as HTMLElement).classList.remove(W_100);
                } else {
                    classes.push(MARGIN.MS_AUTO, MARGIN.ME_0);
                }
                content = createHtmlElement("div", { innerHTML: elts, cssClass: classes });
            }

            const toSelect: SmartTableRow<ItemData>[] = [row];
            const toSelectUids = new Set<string>([row.uid]);
            this._getAllChilrenRecursively(row, toSelectUids, toSelect);

            content.addEventListener("click", (e) => {
                // Stop propagation so row is not selected
                e.stopPropagation();

                // Expand all on select all
                if (this._smartTable.options.hierarchyOptions?.selectChildren?.expandOnSelect) {
                    for (const rowToSelect of toSelect) {
                        if (rowToSelect.hierarchyInfo) {
                            if (rowToSelect.hierarchyInfo.collapsable) {
                                rowToSelect.hierarchyInfo.collapsed = false;
                                rowToSelect.hierarchyInfo.childCollapsed = false;
                            }
                            if (rowToSelect.hierarchyInfo.depth > 0 && rowToSelect.uid !== row.uid) {
                                rowToSelect.hierarchyInfo.ancestorCollapsed = false;
                            }
                        }
                    }

                    this._smartTable.updateDisplayableRows();
                    // Refresh of smartTable will be trigger by timeline callback on 'allCollapseOrExpandEvent'
                    if (!this._smartTable.options.withinTimeline) {
                        this._smartTable.refresh();
                    }
                }

                const selected = children.some(r => !r.selected);
                if (selected) {
                    // Select all
                    this._smartTable.selectRows(Array.from(toSelectUids), { add: true, preventScroll: true, doNotDispatchChangeEvent: Boolean(selectChildrenOptions.doNotDispatchEvent) });
                } else {
                    // Unselect all
                    const newSelection = this._smartTable.getSelectedRows().map(r => r.uid).filter(selectedRowUid => {
                        return !toSelectUids.has(selectedRowUid);
                    });
                    this._smartTable.selectRows(newSelection, { add: false, preventScroll: true, doNotDispatchChangeEvent: Boolean(selectChildrenOptions.doNotDispatchEvent) });
                }

                if (selectChildrenOptions.selectCallback) {
                    const items = toSelect.map(r => getVOFValue(r.data)).filter(i => i != null);
                    selectChildrenOptions.selectCallback(items, selected);
                }
            });

            tdFirstChild.append(content);

            if (selectChildrenOptions.cursor) {
                content.style.cursor = selectChildrenOptions.cursor;
            }

            this.selectChildrenDisplay.set(row, { content, cell, container: tdElement });
        }
    }

    /** Refresh select children content */
    public refreshSelectChildrenContent(e: CustomEvent<SelectionChange<ItemData>>): void {
        if (this._smartTable.options?.hierarchyOptions?.selectChildren) {
            // Set of children for which the selection has changed
            const childrenUidsToCheck = new Set([...e.detail.added, ...e.detail.removed]);
            const parentsToCheck = new Set<SmartTableRow<ItemData>>();
            childrenUidsToCheck.forEach((childUid) => {
                const childRow = this._smartTable.rowsByUid.get(childUid);
                if (childRow) {
                    const parentRow = this.parent.get(childRow);
                    if (parentRow) {
                        parentsToCheck.add(parentRow);
                    }
                }
            });

            parentsToCheck.forEach((parent) => {
                const selectChildren = this.selectChildrenDisplay.get(parent);
                if (selectChildren) {
                    selectChildren.content.remove();

                    this.createSelectUnselect(selectChildren.cell, selectChildren.container);
                }
            });
        }
    }

    /** Get all children recursively */
    private _getAllChilrenRecursively(row: SmartTableRow<ItemData>, alreadyVisitedUids: Set<string>, alreadyVisited: SmartTableRow<ItemData>[]): void {

        const children = this.children.get(row);
        if (children == null) {
            return;
        }

        for (const child of children) {
            if (alreadyVisitedUids.has(child.uid)) {
                this._smartTable.hierarchyLoopEvent.emit({ row });
                return;
            }
            alreadyVisited.push(child);
            alreadyVisitedUids.add(child.uid);

            this._getAllChilrenRecursively(child, alreadyVisitedUids, alreadyVisited);
        }
    }

    /** Return icons to display */
    private _getIconsFromIconName(iconOptions: Partial<IconFaOptions>): { all: IconFaOptions, none: IconFaOptions, some: IconFaOptions } {
        const circleName = { name: getIconFaName(IconFA6.CIRCLE) };
        return {
            all: { ...circleName, ...iconOptions, prefix: FaClasses.SOLID },
            some: { ...circleName, ...iconOptions, prefix: FaClasses.DUOTONE },
            none: { ...circleName, ...iconOptions, prefix: FaClasses.LIGHT }
        };
    }

}
